"use client";

import { useCallback, useEffect, useState } from "react";
import { Button } from "../ui/button";
import { CRAFT_AREA_LABELS } from "../../../shared/craftAreas";
import { userFacingError } from "../../../shared/userFacingError";

type LearnState = {
  countsByArea: Record<string, number>;
  practiced: Array<{ craftArea: string; practicedAt: string; note?: string | null }>;
};

const FOCUS_AREAS = [
  "pacing",
  "conflictTension",
  "characterDevelopment",
  "themeReinforcement",
  "plotProgression",
  "hookEnding",
];

function areaLabel(area: string): string {
  return (CRAFT_AREA_LABELS as Record<string, string>)[area] ?? area;
}

/**
 * Lightweight Learn checklist tied to Story Craft craft areas.
 * Author+ — progress is stored via /api/learn/progress.
 */
export function LearnPanel({
  baseUrl,
  authToken,
  weakAreas = [],
}: {
  baseUrl: string;
  authToken: string | null;
  weakAreas?: string[];
}) {
  const [state, setState] = useState<LearnState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!authToken) return;
    try {
      const res = await fetch(`${baseUrl.replace(/\/$/, "")}/api/learn/progress`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const body = (await res.json().catch(() => ({}))) as LearnState & { error?: string };
      if (!res.ok) {
        setError(userFacingError(body.error || `Learn load failed (${res.status})`, "generic"));
        return;
      }
      setState({ countsByArea: body.countsByArea || {}, practiced: body.practiced || [] });
      setError(null);
    } catch (e) {
      setError(userFacingError(e, "generic"));
    }
  }, [authToken, baseUrl]);

  useEffect(() => {
    void load();
  }, [load]);

  const markPracticed = async (craftArea: string) => {
    if (!authToken) return;
    setBusy(craftArea);
    try {
      const res = await fetch(`${baseUrl.replace(/\/$/, "")}/api/learn/progress`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ craftArea, note: "Practiced from Learn panel" }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(userFacingError(body.error || `Could not save (${res.status})`, "generic"));
      } else {
        await load();
      }
    } finally {
      setBusy(null);
    }
  };

  const areas = Array.from(new Set([...weakAreas, ...FOCUS_AREAS]));

  return (
    <div className="space-y-3 rounded-lg border border-border bg-card p-4 text-sm">
      <div>
        <h3 className="font-semibold text-foreground">Learn</h3>
        <p className="mt-1 text-muted-foreground">
          Context-aware craft practice. Mark an area after you revise a chapter — tips stay linked from
          Story Craft Improve this.
        </p>
      </div>
      {error ? <p className="text-destructive">{error}</p> : null}
      {!authToken ? (
        <p className="text-muted-foreground">Sign in with Author or Editor to track Learn progress.</p>
      ) : (
        <ul className="space-y-2">
          {areas.map((area) => {
            const count = state?.countsByArea?.[area] ?? 0;
            const weak = weakAreas.includes(area);
            return (
              <li
                key={area}
                className="flex items-center justify-between gap-2 rounded-md border border-border/60 px-3 py-2"
              >
                <div>
                  <p className="font-medium text-foreground">
                    {areaLabel(area)}
                    {weak ? (
                      <span className="ml-2 text-xs font-normal text-amber-700 dark:text-amber-400">
                        weak on this chapter
                      </span>
                    ) : null}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Practiced {count} time{count === 1 ? "" : "s"} in last 30 days
                  </p>
                </div>
                <Button
                  type="button"
                  size="xs"
                  disabled={busy === area}
                  onClick={() => void markPracticed(area)}
                >
                  {busy === area ? "Saving…" : "Mark practiced"}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
